/**
 * GeoCalculations - Shared utility for geographic distance calculations
 * 
 * Provides consistent distance calculation methods across the application
 * using the Haversine formula for accuracy
 */

export class GeoCalculations {
  static EARTH_RADIUS_KM = 6371;
  
  /**
   * Calculate distance between two points using the Haversine formula
   * @param {number} lat1 - Latitude of first point
   * @param {number} lon1 - Longitude of first point
   * @param {number} lat2 - Latitude of second point
   * @param {number} lon2 - Longitude of second point
   * @returns {number} Distance in kilometers
   */
  static haversineDistance(lat1, lon1, lat2, lon2) {
    const toRad = deg => deg * Math.PI / 180;
    
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) + 
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * 
              Math.sin(dLon/2) * Math.sin(dLon/2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    
    return this.EARTH_RADIUS_KM * c;
  }
  
  /**
   * Format distance for display with appropriate units
   * @param {number} km - Distance in kilometers
   * @param {number} precision - Number of decimal places (default: 1)
   * @returns {string} Formatted distance string
   */
  static formatDistance(km, precision = 1) {
    if (km === null || km === undefined || isNaN(km)) {
      return '';
    }
    
    // For distances less than 1km, show in meters
    if (km < 1) {
      return `${Math.round(km * 1000)}m`;
    }
    
    // For distances less than 10km, show one decimal place
    if (km < 10) {
      return `${km.toFixed(precision)}km`;
    }
    
    // For longer distances, round to nearest kilometer
    return `${Math.round(km)}km`;
  }
  
  /**
   * Round distance to specified decimal places
   * @param {number} distance - Distance value
   * @param {number} decimals - Number of decimal places (default: 1)
   * @returns {number} Rounded distance
   */
  static roundDistance(distance, decimals = 1) {
    const factor = Math.pow(10, decimals);
    return Math.round(distance * factor) / factor;
  }
  
  /**
   * Check if a point is within a given radius of another point
   * @param {number} centerLat - Center point latitude
   * @param {number} centerLon - Center point longitude
   * @param {number} pointLat - Target point latitude
   * @param {number} pointLon - Target point longitude
   * @param {number} radiusKm - Radius in kilometers
   * @returns {boolean} True if point is within radius
   */
  static isWithinRadius(centerLat, centerLon, pointLat, pointLon, radiusKm) {
    const distance = this.haversineDistance(centerLat, centerLon, pointLat, pointLon);
    return distance <= radiusKm;
  }
  
  /**
   * Sort locations by distance from a reference point
   * @param {Array} locations - Array of location objects with lat/lon properties
   * @param {number} refLat - Reference point latitude
   * @param {number} refLon - Reference point longitude
   * @returns {Array} Sorted array of locations with added distance property
   */
  static sortByDistance(locations, refLat, refLon) {
    return locations
      .map(location => ({
        ...location,
        distance: this.haversineDistance(
          refLat, 
          refLon, 
          location.lat || location.latitude, 
          location.lon || location.longitude || location.lng
        )
      }))
      .sort((a, b) => a.distance - b.distance);
  }
  
  /**
   * Convert degrees to radians
   * @param {number} degrees - Angle in degrees
   * @returns {number} Angle in radians
   */
  static degreesToRadians(degrees) {
    return degrees * Math.PI / 180;
  }
  
  /**
   * Convert radians to degrees
   * @param {number} radians - Angle in radians
   * @returns {number} Angle in degrees
   */
  static radiansToDegrees(radians) {
    return radians * 180 / Math.PI;
  }
}

export default GeoCalculations;